// JavaScript code for sjm-booking/school-search component
// School-name combobox shared by the booking wizard and the general enquiry. Listens on the document,
// so it keeps working inside the wizard's AJAX-swapped steps. Searching starts at 3 characters and
// waits for a pause in typing (the HubSpot search allows about five calls a second per portal).
(() => {
    "use strict";

    const MIN_LENGTH = 3;
    const DEBOUNCE   = 300;

    let timer       = null;
    let sequence    = 0;
    let activeIndex = -1;

    function partsOf(el) {
        const root = el.closest('[data-school-search]');
        if (!root) return null;

        return {
            root,
            input: root.querySelector('[data-school-search-input]'),
            list: root.querySelector('[data-school-search-list]'),
            idField: root.querySelector('[data-school-search-id]'),
            status: root.querySelector('[data-school-search-status]'),
        };
    }

    function escapeHtml(value) {
        return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    function close(parts) {
        parts.list.hidden = true;
        parts.list.innerHTML = '';
        parts.input.setAttribute('aria-expanded', 'false');
        parts.input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
    }

    function open(parts, html, statusText) {
        parts.list.innerHTML = html;
        parts.list.hidden = false;
        parts.input.setAttribute('aria-expanded', 'true');
        activeIndex = -1;
        if (parts.status) parts.status.textContent = statusText || '';
    }

    function pick(parts, option) {
        parts.input.value = option.dataset.name;
        if (parts.idField) parts.idField.value = option.dataset.id || '';
        close(parts);
        // Setting .value doesn't fire "input" - dispatch one so listeners (validation, the booking
        // summary) see the picked name. Flagged so it doesn't start a new search or clear the id.
        parts.input.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: { schoolPicked: true } }));
        parts.input.focus();
    }

    async function search(parts, query) {
        const requestSeq = ++sequence;
        const formData   = new FormData();
        formData.append('action', 'sjm_booking_search_schools');
        formData.append('nonce', parts.root.dataset.nonce);
        formData.append('query', query);

        try {
            const response = await fetch(parts.root.dataset.ajaxUrl, { method: 'POST', body: formData });
            const data     = await response.json();

            if (requestSeq !== sequence || parts.input.value.trim() !== query) return;

            const schools = data.success ? data.data.schools : [];
            const listId  = parts.list.id;

            const options = schools.map((school, index) => `
                <li role="option" id="${listId}-${index}" aria-selected="false" data-school-search-option
                    data-id="${escapeHtml(school.id)}" data-name="${escapeHtml(school.name)}"
                    class="px-[12px] py-[8px] cursor-pointer hover:bg-black/5 aria-selected:bg-black/5">
                    <span class="block text-[15px]">${escapeHtml(school.name)}</span>
                    ${school.city ? `<span class="block text-[13px] text-black/60">${escapeHtml(school.city)}</span>` : ''}
                </li>`);

            // Always offer the typed name, for a school that isn't in the list yet.
            options.push(`
                <li role="option" id="${listId}-${schools.length}" aria-selected="false" data-school-search-option
                    data-id="" data-name="${escapeHtml(query)}"
                    class="px-[12px] py-[8px] cursor-pointer text-[14px] underline hover:bg-black/5 aria-selected:bg-black/5">
                    Not listed? Use "${escapeHtml(query)}"
                </li>`);

            open(parts, options.join(''), schools.length ? `${schools.length} matching schools` : 'No matching schools');
        } catch (_) {
            close(parts);
        }
    }

    document.addEventListener('input', (e) => {
        const input = e.target.closest('[data-school-search-input]');
        if (!input || (e.detail && e.detail.schoolPicked)) return;

        const parts = partsOf(input);
        if (!parts) return;

        // A changed name no longer matches the school picked earlier.
        if (parts.idField) parts.idField.value = '';

        window.clearTimeout(timer);
        const query = input.value.trim();

        if (query.length < MIN_LENGTH) {
            close(parts);
            return;
        }

        open(parts, '<li role="presentation" class="px-[12px] py-[8px] text-[14px] text-black/60">Searching…</li>');
        timer = window.setTimeout(() => search(parts, query), DEBOUNCE);
    });

    document.addEventListener('keydown', (e) => {
        const input = e.target.closest('[data-school-search-input]');
        if (!input) return;

        const parts = partsOf(input);
        if (!parts || parts.list.hidden) return;

        const options = Array.from(parts.list.querySelectorAll('[data-school-search-option]'));

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (!options.length) return;
            e.preventDefault();

            activeIndex = (activeIndex + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
            options.forEach((option, index) => option.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false'));
            input.setAttribute('aria-activedescendant', options[activeIndex].id);
            options[activeIndex].scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' && activeIndex >= 0 && options[activeIndex]) {
            e.preventDefault();
            pick(parts, options[activeIndex]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            close(parts);
        }
    });

    document.addEventListener('click', (e) => {
        const option = e.target.closest('[data-school-search-option]');
        if (option) {
            const parts = partsOf(option);
            if (parts) pick(parts, option);
            return;
        }

        document.querySelectorAll('[data-school-search]').forEach((root) => {
            if (!root.contains(e.target)) {
                const parts = partsOf(root);
                if (parts && !parts.list.hidden) close(parts);
            }
        });
    });
})();
